import global from "../global.js";

const log = global.log.getLogger("f_mtac.scene.renderer");
log.setLevel(log.levels.WARN);

/**
 * @brief Compute position and size of the Airbus logo in a given image
 * @param {*} width, width of the given image
 * @param {*} height, height of the given image
 * @param {float} k, transformation ration (only used for picture with none extended grid)
 * @param {bool} extended_grid true if grid is extended
 */
const Watermark = function (width, height, k, extended_grid) {
    log.debug("Watermark.ctor in ", {width, height, k, extended_grid});
    const logo = document.querySelector(".logo svg").cloneNode(true);
    log.debug("Watermark.ctor ", logo.viewBox.baseVal);
    const logo_style = document.createElement("style");
    let color = global.scene.map.grid.configuration.get("grid.color");
    logo_style.innerHTML = "svg * {fill:" + color + ";fill-opacity:0.4}";
    logo.appendChild(logo_style);

    this.side = {width: width, height: height};

    if (!extended_grid) {
        let scale = (global.app.scale.wb / 150) * global.resources.scale_factor;

        // compute position of the logo in the picture
        let square_size = global.scene.map.grid.configuration.get("square.width");
        this.side.x = width - (square_size + logo.viewBox.baseVal.width * scale);
        this.side.y = height - (square_size + logo.viewBox.baseVal.height * scale);

        // compute size of the watermark
        let ratio = square_size / (logo.viewBox.baseVal.width * scale);
        log.debug("Watermark.ctor", {square_size, scale, ratio});
        this.width = logo.viewBox.baseVal.width * scale < square_size ? logo.viewBox.baseVal.width * k * scale * ratio : logo.viewBox.baseVal.width * k * scale;
        this.height = logo.viewBox.baseVal.width * scale < square_size ? logo.viewBox.baseVal.height * k * scale * ratio : logo.viewBox.baseVal.height * k * scale;
    } else {
        // compute size of the watermark: height should be equal 20
        let logo_scale = logo.viewBox.baseVal.height / 20;
        this.height = logo.viewBox.baseVal.height / logo_scale;
        this.width = logo.viewBox.baseVal.width / logo_scale;
        log.debug("Watermark.ctor", {logo_scale});

        // compute position of the watermark in the window
        // on the top right side of the window, no quadrant here
        this.side.x = width - (80 + this.width);
        this.side.y = 18;
    }

    log.debug("Watermark.ctor out", {width: this.width, height: this.height, side_x: this.side.x, side_y: this.side.y});

    this.export = () => {
        const serializer = new XMLSerializer();
        logo.setAttribute("width", `${this.width}px`);
        logo.setAttribute("height", `${this.height}px`);
        const svg_str = serializer.serializeToString(logo);
        return new Blob([svg_str], {type: "image/svg+xml;charset=utf-8"});
    };
};

//Get all urls of fonts from cache
const getFontFromCache = async function () {
    let all_font = [];
    await caches.keys().then(async function (key_list) {
        await caches.open(key_list[0]).then(async function (response) {
            await response.keys().then(function (response_key) {
                response_key.forEach((line) => {
                    if (line.url.includes("/mtac/static/fonts/roboto")) {
                        all_font.push(line.url.split("?")[0]);
                    }
                });
            });
        });
    });
    return new Promise((resolve) => {
        resolve(all_font);
    });
};

// Split font url to get the last part : https://agamemnon/mtac/static/fonts/roboto-v20-latin-italic.woff = roboto-v20-latin-italic.woff
const splitFontElements = function (font_URL) {
    let splited = font_URL.split("/");
    return splited[splited.length - 1];
};

const getMimeOfFontUrl = function (font_URL) {
    return font_URL.split(".")[1];
};

//Convert font from url to Base64
const convertFileToBlob = async function (font_URL) {
    const response = await fetch(font_URL);
    const data = await response.blob();
    return new Promise((resolve) => {
        const fichier = new File([data], "font", {type: "font/woff2"});
        let reader = new FileReader();
        reader.readAsDataURL(fichier);
        reader.onload = function () {
            resolve(reader.result);
        };
    });
};

const generateRobotoFontFace = async function (font_URL) {
    let font_map = new Map();
    let font_elements = splitFontElements(font_URL);

    if (font_elements.includes("condensed")) {
        font_map.set("font_name", "font-family: 'Roboto Condensed';\n");
    } else {
        font_map.set("font_name", "font-family: 'Roboto';\n");
    }

    if (font_elements.includes("italic")) {
        font_map.set("font_style", "font-style: italic;\n");
    } else {
        font_map.set("font_style", "font-style: normal;\n");
    }

    for (let i = 100; i <= 900; i += 100) {
        if (font_elements.includes(i)) {
            font_map.set("font_weight", "font-weight: " + i + ";\n");
            break;
        }
        font_map.set("font_weight", "");
    }

    let font_face_line = "\n@font-face {\n  " + font_map.get("font_name") + font_map.get("font_style") + font_map.get("font_weight") + "src: url('" + (await convertFileToBlob(font_URL)) + "');\n}";

    return font_face_line;
};

const Renderer = function (svg, map) {
    // log.error("svg ",svg);
    const container_elements = ["svg", "g"];
    const TO_RADIANS = Math.PI / 180;

    const inline_style = (source_node, destination_node) => {
        for (let n = 0; n < destination_node.childNodes.length; n++) {
            let child = destination_node.childNodes[n];
            if (container_elements.indexOf(child.tagName) != -1) {
                inline_style(source_node.childNodes[n], child);
                continue;
            }
            let style = source_node.childNodes[n].currentStyle || window.getComputedStyle(source_node.childNodes[n]);
            if (style == "undefined" || style == null) continue;
            for (let m = 0; m < style.length; m++) {
                child.style.setProperty(style[m], style.getPropertyValue(style[m]));
            }
        }
    };

    const vector_blob_create = async (width, height, objects) => {
        let keep_tgs = keep_tgs_without_objects;
        let keep_ids = keep_ids_without_objects;
        if (objects !== undefined) {
            keep_tgs = keep_tgs_objects;
            keep_ids = keep_ids_objects;
        }
        const serializer = new XMLSerializer();
        const svg_copy = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        $("link").each(function (i, link) {
            log.debug(`Renderer.vector_blob_create link = ${link}`);
            let copy_link = link.cloneNode(true);
            svg_copy.appendChild(copy_link);
        });
        //---------------------------
        const fonts_url = await getFontFromCache();
        const font = window.document.createElement("style");
        font.innerHTML = "";
        for (const url of fonts_url) {
            if (getMimeOfFontUrl(url) === "woff2") {
                // No need for other font type
                font.innerHTML += await generateRobotoFontFace(url);
            }
        }
        svg_copy.appendChild(font);
        //---------------------------
        $("style").each(function (i, style) {
            let copy_style = style.cloneNode(true);
            svg_copy.appendChild(copy_style);
        });
        svg_copy.setAttribute("width", `${width}px`);
        svg_copy.setAttribute("height", `${height}px`);
        for (let n = 0; n < svg.children.length; n++) {
            let src_child = svg.children[n];
            if (keep_tgs.indexOf(src_child.tagName) === -1 || (src_child.tagName === "g" && keep_ids.indexOf(src_child.id) === -1) || src_child.getAttribute("hidden") === "true") {
                continue;
            }
            let dst_child = src_child.cloneNode(true);
            inline_style(src_child, dst_child);
            svg_copy.appendChild(dst_child);
        }
        //log.error("svg_copy ",serializer.serializeToString(svg_copy));
        return new Promise((resolve) => {
            resolve(new Blob([serializer.serializeToString(svg_copy)], {type: "image/svg+xml;charset=utf-8"}));
        });
    };

    /**
     * @brief Creates an HTML canvas element with a given size
     * @param {*} width
     * @param {*} height
     * @returns the canvas and the graphical context of the canvas
     */
    const canvas_init = (width, height) => {
        log.debug("Renderer.canvas_init in", {width, height});
        var canvas = document.createElement("canvas");
        var context = canvas.getContext("2d");
        canvas.width = width;
        canvas.height = height;
        context.clearRect(0, 0, width, height);
        return {canvas, context};
    };

    /**
     * @brief Copy the (width, height) area of a given canvas into a new canvas
     * @param {*} old_canvas
     * @param {*} width
     * @param {*} height
     * @returns the new canvas and its graphical context
     */
    const canvas_crop = (old_canvas, width, height) => {
        var {canvas, context} = canvas_init(width, height);
        context.drawImage(old_canvas, 0, 0, width, height, 0, 0, width, height);
        return {canvas, context};
    };

    this.mime_type = "image/jpeg";
    this.quality = 0.9;

    const canvas_trace = (canvas) => {
        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), this.mime_type, this.quality);
        });
    };

    /**
     * @brief Render a graphic context as is
     * @param {*} context
     * @param {*} blob
     * @param {*} width, width of the context
     * @param {*} height, height of the context
     */
    const context_render_blob_basic = async (context, blob, width, height) => {
        log.debug("Renderer.context_render_blob_basic in", {width, height});
        const image = new Image();
        image.width = width;
        image.height = height;
        return new Promise((resolve) => {
            const blob_url = window.URL.createObjectURL(blob);
            image.onload = function () {
                context.drawImage(
                    image,
                    0,
                    0,
                    width,
                    height, // source area
                    0,
                    0,
                    width,
                    height
                ); // destination area

                resolve();
                window.URL.revokeObjectURL(blob_url);
            };
            image.src = blob_url;
        });
    };

    /**
     * @brief Render a graphic in a given cavas. The source image is scaled with k.
     * @param {*} context
     * @param {*} blob
     * @param {*} x1, x position of the source image
     * @param {*} y1, y position of the source image
     * @param {*} x2, x destination
     * @param {*} y2, y destination
     * @param {*} k, scale transformation
     * @param {*} width, width of the destination image
     * @param {*} height, height of the destination image
     */
    const context_render_blob = async (context, blob, x1, y1, x2, y2, k, width, height) => {
        log.debug("Renderer.context_render_blob in", {width, height, k, x1, y1, x2, y2});
        const image = new Image();
        image.width = k * width;
        image.height = k * height;
        return new Promise((resolve) => {
            const blob_url = window.URL.createObjectURL(blob);
            image.onload = function () {
                context.drawImage(
                    image,
                    x1,
                    y1,
                    k * width,
                    k * height, // source area
                    x2,
                    y2,
                    width,
                    height
                ); // destination area
                resolve();
                window.URL.revokeObjectURL(blob_url);
            };
            image.src = blob_url;
        });
    };

    /**
     * @brief Render the background image with rotation in the center of a given canvas.
     * @param {*} context, graphic context
     * @param {*} blob
     * @param {*} x1, centering correction on x axis
     * @param {*} y1, centering correction on y axis
     * @param {*} k, scale factor to apply to the source image
     * @param {*} width, width of the canvas
     * @param {*} height, height of the canvas
     */
    const context_render_blob_image_with_rotation = async (context, blob, x1, y1, k, width, height) => {
        const image = new Image();
        log.debug("Renderer.context_render_blob_image_with_rotation in", {width, height, x1, y1, k});
        image.width = k * width;
        image.height = k * height;
        let {width: img_width, height: img_height, rotation} = map;
        log.debug("Renderer.context_render_blob_image_with_rotation ", {img_width, img_height});

        return new Promise((resolve) => {
            const blob_url = window.URL.createObjectURL(blob);
            image.onload = function () {
                context.save();

                // translation of the context to the center, with given corrections
                context.translate(width / 2.0 + x1 / 2.0, height / 2.0 + y1 / 2.0);

                // rotation of the image
                context.rotate(rotation * TO_RADIANS);

                // draw to the destination
                context.drawImage(image, (-k * img_width) / 2, (-k * img_height) / 2, k * img_width, k * img_height);

                context.restore();

                resolve();
                window.URL.revokeObjectURL(blob_url);
            };
            image.src = blob_url;
        });
    };

    const blob_download = async (blob, name) => {
        let blob_uri = null;
        // AGPOC-8535 : add a try catch to not beeing blocked if createObjectURL failed
        try {
            blob_uri = window.URL.createObjectURL(blob);
        } catch (error) {
            log.error("export failed", error);
        }
        let link = document.createElement("a");
        link.href = blob_uri;
        link.download = name;
        link.click();
        window.URL.revokeObjectURL(blob_uri);
    };

    const keep_tgs_without_objects = ["defs", "g"];
    const keep_ids_without_objects = ["grid", "frame", "axis", "quadrant", "distance", "sub_axis", "sub_grid"];

    const keep_tgs_objects = ["defs", "g"];
    const keep_ids_objects = ["grid", "frame", "axis", "quadrant", "distance", "scene", "notes", "resources", "sub_axis", "sub_grid"];

    const export_date = () => {
        var time_date = new Date(Date.now());
        var D = time_date.getDate();
        var Mo = time_date.getMonth() + 1;
        var Y = time_date.getFullYear();
        var H = time_date.getHours();
        var Min = time_date.getMinutes();
        return `${Y}-${Mo >= 10 ? Mo : "0" + Mo}-${D >= 10 ? D : "0" + D}_${H >= 10 ? H : "0" + H}-${Min >= 10 ? Min : "0" + Min}`;
    };

    const hex_to_rgb = (hex) => {
        let r = 0,
            g = 0,
            b = 0;
        // handling 3 digit hex
        if (hex.length == 4) {
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
            // handling 6 digit hex
        } else if (hex.length == 7) {
            r = parseInt(hex[1] + hex[2], 16);
            g = parseInt(hex[3] + hex[4], 16);
            b = parseInt(hex[5] + hex[6], 16);
        }
        return {
            red: r,
            green: g,
            blue: b
        };
    };

    /**
     *
     * @brief Build and download the 2 canvas, one without resources, the other with resources
     * Assumes that center_to_box has been called programatically
     */
    this.render = async () => {
        if (!map.loaded) {
            // nothing to export
            return;
        }
        log.debug("Renderer.render in");

        let tracked = global.scene.tracking.tracked;
        global.scene.tracking.untrack();

        if (map.is_gis === false && map.grid.is_extended === true) {
            await render_geolocalized_picture();
        } else {
            await render_not_geolocalized_picture();
        }

        if (tracked !== undefined) {
            global.scene.tracking.track(tracked);
        }
    };

    /**
     * @brief Embed a logo with a name in a given canvas context for a picture
     * @param {*} context
     * @param {*} width
     * @param {*} height
     * @param {*} k
     * @param {*} name
     */
    const render_watermark = async (context, width, height, k, name) => {
        log.debug("Renderer.render_watermark in", {width, height, k, name});

        const colors = map.grid.configuration.get("grid.colors");
        const color_by_value = Object.fromEntries(colors.map((color) => [color.value, color]));
        const watermark = new Watermark(width, height, k, map.grid.is_extended);
        const watermark_blob = watermark.export();

        let scale;
        if (map.is_gis === false && map.grid.is_extended === true) {
            // gereferenced picture
            scale = 1;
            context.font = "16px Roboto Condensed";
        } else {
            // not georeferenced picture
            scale = ((3 * global.app.scale.wb) / 150) * global.resources.scale_factor;
            context.scale(scale, scale);
            context.fontFamily = "Roboto Condensed";
            log.debug("Renderer.render_watermark: scale =", scale);
        }
        await context_render_blob(context, watermark_blob, 0, 0, watermark.side.x / scale, watermark.side.y / scale, 1, watermark.width, watermark.height);

        let color = global.scene.map.grid.configuration.get("grid.color");
        if (color in color_by_value) {
            color = color_by_value[color].value;
        }
        if (color === "white") {
            // verrue car fichier de config ne contient pas les valeurs hexa des couleurs
            color = "#fff";
        }
        let rgb = hex_to_rgb(color);
        context.fillStyle = `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.4)`;
        context.textAlign = "right";
        let posx = watermark.side.x / scale + watermark.width;
        let posy = watermark.side.y / scale + (3 / 2) * watermark.height;

        log.debug("Renderer.render_watermark");
        context.fillText(name, posx, posy);
    };

    /**
     * @brief download two image files for basic pictures without geolocation
     */
    const render_not_geolocalized_picture = async () => {
        const {width, height} = map;
        const {x, y, k} = map.transform.state;
        log.debug("map.fallback_media_id  ", map.map_gis.fallback_media_id);
        log.debug("map.media_id ", map.media_id);
        let media_id = global.view.is_gis ? map.map_gis.fallback_media_id : map.media_id;
        log.debug("media_id ", media_id);
        const media = await global.medias.load_media({local_id: media_id});

        // in GIS case, the media may not exist yet (can happened if we ask the export too soon after sitac creation)
        // to not beeing blocked => release the modal
        if (media === undefined || media.data === undefined) {
            return;
        }

        const raster_blob = media.data.media_data;
        const vector_blob = await vector_blob_create(x + k * width, y + k * height);
        const vector_objects_blob = await vector_blob_create(x + k * width, y + k * height, true);
        //blob_dump_data_uri(vector_objects_blob);
        log.debug("Renderer.render_not_geolocalized_picture map:", {width, height, x, y, k});

        // we need to initialize the canvas size to the maximum possible value between
        // the raster image and the grid to contain both of them
        let {canvas, context} = canvas_init(Math.max(1, k) * width + x, Math.max(1, k) * height + y);
        await context_render_blob(context, raster_blob, 0, 0, 0, 0, 1, width, height);
        await context_render_blob(context, vector_blob, x, y, 0, 0, k, width, height);
        ({canvas, context} = canvas_crop(canvas, width, height));

        const name = (await global.storage.selected_context.name) || "unnamed";
        await render_watermark(context, width, height, k, name);

        let file_name = `${name}_without_objects_${export_date()}.${this.mime_type.split("/")[1]}`;
        blob_download(await canvas_trace(canvas), file_name);

        ({canvas, context} = canvas_crop(canvas, width, height));
        await context_render_blob(context, vector_objects_blob, x, y, 0, 0, k, width, height);
        file_name = `${name}_complete_${export_date()}.${this.mime_type.split("/")[1]}`;
        blob_download(await canvas_trace(canvas), file_name);
    };

    /**
     * @brief download two image files for geolocalized pictures with extended grid
     */
    const render_geolocalized_picture = async () => {
        const {width, height} = map;
        const {x, y, k} = map.transform.state;
        log.debug("Renderer.render_geolocalized_picture", {x, y, k, width, height});
        let media_id = map.type === "gis" ? map.fallback_media_id : map.media_id;
        const media = await global.medias.load_media({local_id: media_id});

        // blob for the image
        const raster_blob = media.data.media_data;

        // get the inbound window for the margins
        let iw = global.view.compute_inbound_window();
        let W = global.view.width;
        let H = global.view.height;
        let vector_blob = await vector_blob_create(W, H);

        // render picture and grid
        let {canvas, context} = canvas_init(Math.max(1, k) * W, Math.max(1, k) * H);
        await context_render_blob_image_with_rotation(context, raster_blob, 0, iw.top - iw.bottom, k, W, H);
        await context_render_blob_basic(context, vector_blob, W, H);

        // clone the canvas
        ({canvas, context} = canvas_crop(canvas, W, H));

        // render watermark
        const name = (await global.storage.selected_context.name) || "unnamed";
        await render_watermark(context, W, H, k, name);

        // download the file without objects
        let file_name = `${name}_without_objects_${export_date()}.${this.mime_type.split("/")[1]}`;
        blob_download(await canvas_trace(canvas), file_name);

        // clone the canvas
        ({canvas, context} = canvas_crop(canvas, W, H));

        // render the objects
        let vector_objects_blob = await vector_blob_create(W, H, true);
        await context_render_blob_basic(context, vector_objects_blob, W, H);
        file_name = `${name}_complete_${export_date()}.${this.mime_type.split("/")[1]}`;

        // download the file with objects
        blob_download(await canvas_trace(canvas), file_name);
    };
};

export default Renderer;
